import { ChevronDownMini } from '@medusajs/icons';
import { useNavigate, useParams } from 'react-router-dom';

import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';
import type { CategoryHandleConfig } from './types';

export type { CategoryHandleConfig } from './types';

// Simplified category type for now
export interface ProductCategory {
  id: string;
  name: string;
  handle: string;
  rank?: number;
  category_children?: ProductCategory[];
}

const isGroupCategory = (handle: string, config: CategoryHandleConfig) =>
  [config.brands, config.od].includes(handle);

const NavItem = ({
  item,
  categoryHandleConfig,
}: {
  item: ProductCategory;
  categoryHandleConfig: CategoryHandleConfig;
}) => {
  const navigate = useNavigate();
  const { countryCode } = useParams<{ countryCode: string }>();
  const code = countryCode || DEFAULT_COUNTRY_CODE;

  const navToProductPageByHandle = (handle: string) => {
    navigate(`/${code}/store/${handle}`);
  };

  return (
    <div
      onClick={() => {
        if (isGroupCategory(item.handle, categoryHandleConfig)) {
          return;
        }
        navToProductPageByHandle(item.handle);
      }}
      className="text-base font-normal group/item hover:bg-primary-60 relative flex h-full w-full cursor-pointer items-center justify-center gap-1 whitespace-nowrap px-1 py-2 text-center md:flex-1"
      data-testid={`nav-category-${item.handle}`}
    >
      {item.name}
      {(item.category_children?.length ?? 0) !== 0 && <ChevronDownMini />}
      {(item.category_children?.length ?? 0) !== 0 && (
        <div className="bg-white-10 invisible absolute left-[50%] top-[100%] w-[218px] translate-x-[-50%] opacity-0 shadow-md transition-all duration-75 ease-in-out group-hover/item:visible group-hover/item:opacity-100">
          {item.category_children?.map((child) => {
            return (
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  if (isGroupCategory(item.handle, categoryHandleConfig)) {
                    navToProductPageByHandle(child.handle);
                  } else {
                    navToProductPageByHandle(`${item.handle}/${child.handle}`);
                  }
                }}
                className="text-grey-60 hover:bg-white-30 hover:text-primary-50 cursor-pointer whitespace-nowrap py-2 text-center"
                key={child.id}
                data-testid={`nav-subcategory-${child.handle}`}
              >
                {child.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export interface ProductNavProps {
  categories: ProductCategory[];
  categoryHandleConfig: CategoryHandleConfig;
}

export const ProductNav = ({
  categories,
  categoryHandleConfig,
}: ProductNavProps) => {
  return (
    <div className="bg-primary-50 hidden w-full md:block">
      <div className="text-white-10 mx-auto flex h-full max-w-[1500px] flex-wrap items-center gap-4 lg:justify-between">
        {categories
          .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
          .map((item) => {
            return (
              <NavItem
                key={item.handle}
                item={item}
                categoryHandleConfig={categoryHandleConfig}
              />
            );
          })}
      </div>
    </div>
  );
};

export default ProductNav;
